import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import {
    ReportItemCreateRequestDto,
    ReportItemDefinitionUpdateRequestDto,
    ReportItemEntity,
    ReportItemMetadataUpdateRequestDto,
    ReportItemResponseDto,
    ReportItemShareRequestDto
} from '../../../report-persistence.model';
import { ReportKey } from '../../../reports/report.registry';

@Injectable({
    providedIn: 'root'
})
export class ReportItemService {
    private httpClient = inject(HttpClient);

    get<TDefinition = unknown>(reportKey: ReportKey, subscriberKey: number | null): Observable<ReportItemEntity<TDefinition>[]> {
        return this.httpClient.get<ReportItemResponseDto[]>(`api/common/reports/${reportKey}/items`, {
            params: this.getSubscriberParams(subscriberKey)
        }).pipe(
            map(items => items.map(item => this.parseItemDefinition<TDefinition>(item)))
        );
    }

    create<TDefinition = unknown>(reportKey: ReportKey, item: ReportItemCreateRequestDto<TDefinition>): Observable<number> {
        const request: ReportItemCreateRequestDto<string> = {
            ...item,
            itemDefinition: this.stringifyItemDefinition(item.itemDefinition)
        };

        return this.httpClient.post<number>(`api/common/reports/${reportKey}/items`, request);
    }

    updateDefinition<TDefinition = unknown>(
        reportKey: ReportKey,
        itemKey: number,
        subscriberKey: number | null,
        itemDefinition: TDefinition | null
    ): Observable<number> {
        const request: ReportItemDefinitionUpdateRequestDto<string> = {
            itemDefinition: this.stringifyItemDefinition(itemDefinition)
        };

        return this.httpClient.put<number>(`api/common/reports/${reportKey}/items/${itemKey}/definition`, request, {
            params: this.getSubscriberParams(subscriberKey)
        });
    }

    updateMetadata(
        reportKey: ReportKey,
        itemKey: number,
        subscriberKey: number | null,
        metadata: ReportItemMetadataUpdateRequestDto
    ): Observable<number> {
        return this.httpClient.put<number>(`api/common/reports/${reportKey}/items/${itemKey}/metadata`, metadata, {
            params: this.getSubscriberParams(subscriberKey)
        });
    }

    share(
        reportKey: ReportKey,
        itemKey: number,
        subscriberKey: number | null,
        recipientUserId: string
    ): Observable<number> {
        const request: ReportItemShareRequestDto = { recipientUserId };

        return this.httpClient.post<number>(`api/common/reports/${reportKey}/items/${itemKey}/share`, request, {
            params: this.getSubscriberParams(subscriberKey)
        });
    }

    delete(reportKey: ReportKey, itemKey: number, subscriberKey: number | null): Observable<void> {
        return this.httpClient.delete<void>(`api/common/reports/${reportKey}/items/${itemKey}`, {
            params: this.getSubscriberParams(subscriberKey)
        });
    }

    private getSubscriberParams(subscriberKey: number | null): HttpParams {
        let params = new HttpParams();
        if (subscriberKey != null) {
            params = params.set('subscriberKey', subscriberKey);
        }

        return params;
    }

    private parseItemDefinition<TDefinition>(item: ReportItemResponseDto): ReportItemEntity<TDefinition> {
        return {
            ...item,
            itemDefinition: this.parseDefinition<TDefinition>(item.itemDefinition)
        };
    }

    private parseDefinition<TDefinition>(itemDefinition: string | null): TDefinition | null {
        if (itemDefinition == null || itemDefinition.trim() === '') {
            return null;
        }

        return JSON.parse(itemDefinition) as TDefinition;
    }

    private stringifyItemDefinition<TDefinition>(itemDefinition: TDefinition | null): string | null {
        if (itemDefinition == null) {
            return null;
        }

        return JSON.stringify(itemDefinition);
    }
}
